/** Generated cover art — for demo records, and for tracks with no usable artwork. */

import { hslToRgb, rgbHex } from '../lib/color.ts';
import { clamp } from '../lib/math.ts';
import { hashStr, mulberry32 } from '../lib/random.ts';
import { canvas, ctx2d } from './canvas.ts';
import { COVER_PAINTERS, COVER_STYLES, type CoverCtx, type CoverStyle } from './cover-styles.ts';

export interface CoverOptions {
  size?: number;
  palette?: { hue: number; sat: number; style?: string } | null;
}

const isStyle = (s: string | undefined): s is CoverStyle =>
  !!s && (COVER_STYLES as readonly string[]).includes(s);

/** Film grain, so a cover never looks like flat CSS. */
function grain(g: CanvasRenderingContext2D, size: number, rand: () => number): void {
  const img = g.getImageData(0, 0, size, size);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (rand() - 0.5) * 16;
    d[i] = (d[i] as number) + n;
    d[i + 1] = (d[i + 1] as number) + n;
    d[i + 2] = (d[i + 2] as number) + n;
  }
  g.putImageData(img, 0, 0);
}

export function generateCover(
  seed: string | number,
  { size = 512, palette = null }: CoverOptions = {},
): HTMLCanvasElement {
  const rand = mulberry32(typeof seed === 'string' ? hashStr(seed) : seed | 0);
  const c = canvas(size);
  const g = ctx2d(c, { willReadFrequently: true });

  const baseHue = palette?.hue ?? Math.floor(rand() * 360);
  const sat = palette?.sat ?? 0.42 + rand() * 0.38;
  const style = isStyle(palette?.style)
    ? palette.style
    : (COVER_STYLES[Math.floor(rand() * COVER_STYLES.length)] as CoverStyle);

  /* Lightness is capped short of white: a blown-out centre reads badly once
     the cover is shrunk onto a record label. */
  const hue = (offset: number, l: number, satMul = 1): string =>
    rgbHex(hslToRgb(baseHue + offset, clamp(sat * satMul, 0, 1), clamp(l, 0.05, 0.82)));

  const bg = g.createLinearGradient(0, 0, size, size);
  bg.addColorStop(0, hue(12, 0.2, 0.9));
  bg.addColorStop(1, hue(-26, 0.07, 1.05));
  g.fillStyle = bg;
  g.fillRect(0, 0, size, size);

  const paint: CoverCtx = { g, size, rand, hue };
  g.save();
  COVER_PAINTERS[style](paint);
  g.restore();
  g.globalAlpha = 1;

  grain(g, size, rand);

  const vg = g.createRadialGradient(
    size / 2,
    size / 2,
    size * 0.25,
    size / 2,
    size / 2,
    size * 0.78,
  );
  vg.addColorStop(0, 'rgba(0,0,0,0)');
  vg.addColorStop(1, 'rgba(0,0,0,0.36)');
  g.fillStyle = vg;
  g.fillRect(0, 0, size, size);

  return c;
}
